import { LocaleLink } from '../../i18n/locale-link';

export interface RankedItem {
  label: string;
  /** Raw magnitude — normalizes the bar width. */
  value: number;
  /** Pre-formatted display value (e.g. "1,2 млрд"). */
  valueText: string;
  /** Optional secondary line (e.g. "42 закупки"). */
  sub?: string;
  /** Makes the label a link (locale-prefixed) — e.g. a supplier's profile. */
  href?: string;
}

/**
 * Horizontal ranked bars for a single magnitude series (budget/count by
 * region/sector). Semantic HTML (a labelled list — natively accessible, no
 * role needed); one hue (brand); recessive track; values in ink tokens.
 */
export function RankedBars({ items }: { items: RankedItem[] }) {
  if (items.length === 0) return <div />;
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <ul className="flex flex-col gap-3">
      {items.map((it) => (
        <li key={it.label}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-[13px]">
            {it.href ? (
              <LocaleLink
                to={it.href}
                title={it.label}
                className="min-w-0 flex-1 truncate font-medium text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand"
              >
                {it.label}
              </LocaleLink>
            ) : (
              <span className="min-w-0 flex-1 truncate font-medium text-ink-soft">{it.label}</span>
            )}
            <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-ink">
              {it.valueText}
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-pill bg-bone">
            <div
              className="h-full rounded-pill bg-brand"
              style={{ width: `${Math.max(2, (it.value / max) * 100)}%` }}
            />
          </div>
          {it.sub && <div className="mt-0.5 text-[11px] text-ink-muted">{it.sub}</div>}
        </li>
      ))}
    </ul>
  );
}
